import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { yupResolver } from '@hookform/resolvers/yup';
import { differenceInDays, isValid, parse as parseDate } from 'date-fns';
import * as yup from 'yup';

import { type ForgotToScanInput } from '../../utils';

export const useForgotToScanForm = (props: UseForgotToScanFormProps) => {
  const { onSubmit } = props;
  const { formatMessage } = useIntl();

  const validationSchema = useMemo(() => {
    return yup
      .object({
        zipCode: yup.string().required(formatMessage(messages.zipCodeRequired)),
        orderId: yup.string().required(formatMessage(messages.orderIdRequired)),
        purchaseDate: yup
          .string()
          .test(
            'valid-date',
            formatMessage(messages.invalidDate),
            validatePurchaseDate,
          ),
      })
      .required();
  }, [formatMessage]);

  const form = useForm<ForgotToScanInput>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      zipCode: '',
      orderId: '',
      purchaseDate: '',
      orderTotal: '',
    },
  });

  const { handleSubmit } = form;

  const submitForm = useMemo(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  // ────────────────────────────────────────────────────────────────────

  return { form, submitForm };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const validatePurchaseDate = (value?: string) => {
  if (!value) return false;

  const purchaseDate = parseDate(value, 'MM/dd/yyyy', new Date());

  if (!isValid(purchaseDate)) return false;

  const diffDays = Math.abs(differenceInDays(new Date(), purchaseDate));

  return diffDays <= 30;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  zipCodeRequired: {
    defaultMessage: 'Zip code is required.',
    description: 'Forgot To Scan | Form | Zip code required',
  },
  orderIdRequired: {
    defaultMessage: 'Order ID is required.',
    description: 'Forgot To Scan | Form | Order id required',
  },
  invalidDate: {
    defaultMessage: 'This order is outside the 30 day window.',
    description: 'Forgot To Scan | Form | Invalid date',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseForgotToScanFormProps = {
  onSubmit: (payload: ForgotToScanInput) => void;
};
